// Auth state. Token + caller summary persisted in localStorage so a refresh
// keeps you signed in. The shape mirrors TokenOut from the backend.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { TokenOut } from "./api";
import { setToken } from "./api";

const SESSION_STORAGE_KEY = "scholarpetition.session";
const SIGNED_OUT_MARKER_KEY = "scholarpetition.signedOutAt";

// Case-progress keys cleared on sign-out so a shared computer (common in
// academia — lab workstations, library terminals, postdoc desks) doesn't
// leak the previous user's funnel state to whoever signs in next.
const CASE_PROGRESS_KEYS = [
  "conv-step",      // conversion funnel position
  "sp-done",        // Case Plan completed stages
  "vg-paid",        // payment marker
  "i140-step",      // I-140 form position
  "i140-max",       // I-140 furthest reached
  "i140-jump",      // I-140 deep-link flag
  "ls-flow-v1",     // Letter Studio wizard state
  "ev-step",        // Evidence & Criteria step
  "vg-outcome",     // demo eligibility outcome override
];

export interface Session {
  token: string;
  expiresAt: string;
  tenantId: string;
  tenantDisplayName: string;
  userEmail: string;
  role: string;
}

interface AuthContextValue {
  session: Session | null;
  signIn: (token: TokenOut) => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    if (new Date(parsed.expiresAt).getTime() < Date.now()) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeSession(s: Session | null) {
  if (s === null) localStorage.removeItem(SESSION_STORAGE_KEY);
  else localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(s));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => {
    const s = readSession();
    if (s) setToken(s.token);
    return s;
  });

  const signIn = useCallback((tok: TokenOut) => {
    const s: Session = {
      token: tok.access_token,
      expiresAt: tok.expires_at,
      tenantId: tok.tenant_id,
      tenantDisplayName: tok.tenant_display_name,
      userEmail: tok.user_email,
      role: tok.role,
    };
    writeSession(s);
    setToken(s.token);
    setSession(s);
  }, []);

  const signOut = useCallback(() => {
    writeSession(null);
    setToken(null);
    setSession(null);
    // Clear case-progress so the next person at this device starts fresh,
    // and leave a short-lived marker /sign-in can read to show "Signed out".
    try {
      CASE_PROGRESS_KEYS.forEach((k) => localStorage.removeItem(k));
      sessionStorage.setItem(SIGNED_OUT_MARKER_KEY, String(Date.now()));
    } catch {
      // localStorage/sessionStorage unavailable (private mode quirk) — ignore.
    }
  }, []);

  // Auto-sign-out on token expiry while the tab is open.
  useEffect(() => {
    if (!session) return;
    const ms = new Date(session.expiresAt).getTime() - Date.now();
    if (ms <= 0) {
      signOut();
      return;
    }
    const id = window.setTimeout(signOut, ms);
    return () => window.clearTimeout(id);
  }, [session, signOut]);

  const value = useMemo(
    () => ({ session, signIn, signOut }),
    [session, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
